import { useAuth } from 'client/lib/auth';
import { RuzcalMgmtPage } from './mgmt-page';
import { focusRef } from 'client/utils/autofocus';
import { PageContent, PageHeading, PageSection, defCalRoute } from './common';
import { AsyncForm } from '@components/async-form';
import { LoadedProps, RouteLoadProps, useRouter } from '@components/router';
import { showModalForm } from '@components/modal-form';
import { useAsyncData } from 'client/lib/hooks';
import { BaseDialog, DialogHeader } from '@components/dialog';
import { DefaultSpinner } from '@components/spinner';
import { EventTypeCategoryRow } from 'server/types/cal-schema';
import { useState } from 'preact/hooks';
import { rpx } from 'client/lib/rpx-client';
import { showError } from '@components/app-error';
import { Toggle } from '@components/toggle';
import { BtnPrimary, Button } from '@components/buttons';

type Props = LoadedProps<typeof load>;
type State = Props['state'];
const defaultName = 'Custom Category';
export const route = defCalRoute({ load, authLevel: 'guide', Page });

async function load(opts: Pick<RouteLoadProps, 'auth' | 'params'>) {
  const { id } = opts.params;
  const user = opts.auth.user!;
  return {
    id,
    name: '',
    description: '',
    hostId: user.id,
    isDefault: false,
    createdAt: new Date(),
    updatedAt: new Date(),
  };
}

function EventCategoriesForm({
  state,
  setState,
  onSave,
  onCancel,
}: {
  state: Props['state'];
  setState: Props['setState'];
  onCancel?(): void;
  onSave?(result: EventTypeCategoryRow): void;
}) {
  const router = useRouter();
  return (
    <AsyncForm
      class="flex flex-col gap-8 max-w-2xl"
      onSubmit={async () => {
        try {
          const result = await rpx.ruzcalEventTypes.saveEventTypeCategory({
            name: state.name.trim() || defaultName,
            id: state.id === 'new' ? undefined : state.id,
            description: state.description.trim(),
            isDefault: state.isDefault,
          });
          if (onSave) {
            onSave(result);
          } else {
            router.goto('/calendar/event-types');
            await new Promise((r) => setTimeout(r, 1000));
          }
        } catch (err) {
          showError(err);
        }
      }}
    >
      <label class="flex flex-col gap-1 max-w-80">
        <span class="font-medium">Name of Category</span>
        <input
          type="text"
          name="name"
          class="inline-ruz-input px-4 p-3 text-sm"
          value={state.name}
          placeholder={`e.g. ${defaultName}`}
          ref={focusRef}
          onInput={(e: any) => setState((s) => ({ ...s, name: e.target.value }))}
        />
      </label>
      <footer class="flex flex-col gap-4">
        <div>
          <label class="inline-flex items-center gap-4 cursor-pointer select-none">
            <Toggle
              class="scale-110"
              checked={state.isDefault}
              onClick={() => setState((s) => ({ ...s, isDefault: !s.isDefault }))}
            />
            Make this your default category
          </label>
        </div>
      </footer>
      <footer class="flex gap-4">
        <BtnPrimary class="rounded-full">Save Event Category</BtnPrimary>
        <Button
          href="/calendar/event-types"
          onClick={onCancel}
          class="text-inherit inline-flex items-center justify-center hover:bg-gray-100 px-4 transition-all rounded-full"
        >
          Cancel
        </Button>
      </footer>
    </AsyncForm>
  );
}

export function showAddCategoriesModal() {
  return showModalForm<EventTypeCategoryRow | undefined>(({ resolve }) => {
    const auth = useAuth();
    const data = useAsyncData(() => load({ params: { id: 'new' }, auth }), []);

    function LoadedForm(data: State) {
      const [state, setState] = useState(data);
      return (
        <div class="p-10">
          <DialogHeader
            title="Create a new event category"
            subtitle="create a new category for grouping the event types."
          />
          <EventCategoriesForm
            state={state}
            setState={setState}
            onSave={(result) => {
              resolve(result);
            }}
            onCancel={() => {
              resolve();
            }}
          />
        </div>
      );
    }

    return (
      <BaseDialog onClose={resolve}>
        {data.data && <LoadedForm {...data.data} />}
        {!data.data && (
          <div class="aspect-square flex flex-col items-center justify-center">
            <DefaultSpinner />
          </div>
        )}
      </BaseDialog>
    );
  });
}

function Page({ state, setState }: Props) {
  return (
    <RuzcalMgmtPage title="Add New Category" currentPage="eventtypes">
      <PageContent>
        <PageSection>
          <header>
            <PageHeading title="Add New Category" />
          </header>
          <EventCategoriesForm state={state} setState={setState} />
        </PageSection>
      </PageContent>
    </RuzcalMgmtPage>
  );
}
